import { Component } from '@theme/component';

/**
 * Packaging mascot that comes alive the first time it scrolls into view. Characters with an act
 * (`data-act`) perform it once; the rest assemble from their tile pieces. Pieces render in place
 * by default; the figure only hides them once it is armed, so markup stays complete without
 * JavaScript and under reduced motion.
 *
 * @extends {Component}
 */
export class MascotFigure extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('data-animate') || this.dataset.state === 'assembled') return;
    if (this.hasAttribute('data-performing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!this.hasAttribute('data-act')) this.dataset.state = 'waiting';
    this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: 0.35 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#observer?.disconnect();

    if (this.hasAttribute('data-act')) {
      this.toggleAttribute('data-performing', true);
      return;
    }

    // One frame in the waiting state guarantees the transition has a start value.
    requestAnimationFrame(() => {
      this.dataset.state = 'assembled';
    });
  };
}

if (!customElements.get('mascot-figure')) {
  customElements.define('mascot-figure', MascotFigure);
}
